import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import Layout from '../components/layout/Layout';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import { Input, Select, Textarea } from '../components/common/FormField';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import FileShaClaimButton from '../components/common/FileShaClaimButton';
import api from '../services/api';
import { fmtDate, fmtDateTime } from '../utils/helpers';
import {useTranslation} from 'react-i18next';

const RISK_LEVELS = ['NONE', 'LOW', 'MODERATE', 'HIGH'];
const RISK_CSS: Record<string, string> = { NONE: 'bg-slate-100 text-slate-500', LOW: 'bg-blue-100 text-[#02357A]', MODERATE: 'bg-amber-100 text-amber-700', HIGH: 'bg-red-100 text-red-700' };
const SESSION_TYPES = ['INDIVIDUAL', 'GROUP', 'FAMILY', 'COUPLES'];

export default function MentalHealthPage() {
  const {t}=useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState<any>({});
  const [patient, setPatient] = useState<any>(null);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [selAssessment, setSelAssessment] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [plan, setPlan] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const notify = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const [showAssessment, setShowAssessment] = useState(false);
  const defaultAssessmentForm = { patientId: '', presentingComplaint: '', historyOfPresentIllness: '', pastPsychiatricHistory: '', substanceUseHistory: '', familyHistory: '', suicideRisk: 'NONE', selfHarmRisk: 'NONE', harmToOthersRisk: 'NONE', riskNotes: '', diagnosisCode: '', diagnosisText: '', managementPlan: '', status: 'ACTIVE' };
  const [assessmentForm, setAssessmentForm] = useState<any>(defaultAssessmentForm);

  const [showSession, setShowSession] = useState(false);
  const defaultSessionForm = { patientId: '', assessmentId: '', sessionDate: new Date().toISOString().slice(0, 16), sessionType: 'INDIVIDUAL', durationMinutes: 45, notes: '', goalsForNextSession: '', nextSessionDate: '' };
  const [sessionForm, setSessionForm] = useState<any>(defaultSessionForm);

  const [showPlan, setShowPlan] = useState(false);
  const defaultPlanForm = { patientId: '', assessmentId: '', goals: '', interventions: '', reviewDate: '', status: 'ACTIVE' };
  const [planForm, setPlanForm] = useState<any>(defaultPlanForm);

  const loadStats = useCallback(async () => {
    try { const r = await api.getMentalHealthStats(); setStats(r?.data || {}); } catch { }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const loadAssessments = useCallback(async (patientId: string) => {
    try {
      const r = await api.getPsychAssessments({ patientId });
      setAssessments(Array.isArray(r?.data) ? r.data : (r?.data?.data || []));
    } catch { setAssessments([]); }
  }, []);

  const loadSessions = useCallback(async (patientId: string) => {
    try {
      const r = await api.getCounselingSessions(patientId);
      setSessions(Array.isArray(r?.data) ? r.data : (r?.data?.data || []));
    } catch { setSessions([]); }
  }, []);

  const loadPlan = useCallback(async (patientId: string) => {
    try { const r = await api.getMentalHealthPlan(patientId); setPlan(r?.data || null); } catch { setPlan(null); }
  }, []);

  useEffect(() => {
    if (patient) {
      loadAssessments(patient.id);
      loadSessions(patient.id);
      loadPlan(patient.id);
      setSelAssessment(null);
    } else {
      setAssessments([]); setSessions([]); setPlan(null); setSelAssessment(null);
    }
  }, [patient, loadAssessments, loadSessions, loadPlan]);

  const saveAssessment = async () => {
    if (!patient) { notify('⚠️ Select a patient first'); return; }
    if (!assessmentForm.presentingComplaint) { notify('⚠️ Presenting complaint required'); return; }
    setSaving(true);
    try {
      const r = await api.createPsychAssessment({ ...assessmentForm, patientId: patient.id, doctorId: user?.id });
      notify(r?.data?.suicideRisk === 'HIGH' || r?.data?.selfHarmRisk === 'HIGH' || r?.data?.harmToOthersRisk === 'HIGH' ? '⚠️ Assessment saved — HIGH risk flagged' : '✅ Assessment saved!');
      setShowAssessment(false); setAssessmentForm(defaultAssessmentForm);
      await loadAssessments(patient.id); loadStats();
      if (r?.data) setSelAssessment(r.data);
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const saveSession = async () => {
    if (!patient) { notify('⚠️ Select a patient first'); return; }
    setSaving(true);
    try {
      await api.createCounselingSession({ ...sessionForm, patientId: patient.id, assessmentId: selAssessment?.id || sessionForm.assessmentId, counselorId: user?.id });
      notify('✅ Counseling session recorded!'); setShowSession(false); setSessionForm(defaultSessionForm);
      loadSessions(patient.id); loadStats();
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const savePlan = async () => {
    if (!patient) { notify('⚠️ Select a patient first'); return; }
    setSaving(true);
    try {
      await api.saveMentalHealthPlan({ ...planForm, patientId: patient.id, assessmentId: selAssessment?.id || planForm.assessmentId });
      notify('✅ Care plan saved!'); setShowPlan(false);
      loadPlan(patient.id);
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  return (
    <Layout title="Mental Wellness" actions={
      <div className="flex gap-2">
        <button onClick={() => { if (!patient) { notify('⚠️ Select a patient first'); return; } setPlanForm({ ...defaultPlanForm, ...(plan || {}) }); setShowPlan(true); }} className="btn-secondary text-sm">📋 Care Plan</button>
        <button onClick={() => { setAssessmentForm(defaultAssessmentForm); setShowAssessment(true); }} className="btn-primary text-sm">+ New Assessment</button>
      </div>
    }>
      {msg && <div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50 max-w-sm">{msg}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {[['High-Risk Active Cases', stats.highRiskActive ?? '—', 'red'], ['Sessions Today', stats.sessionsToday ?? '—', 'blue'], ['Active Cases', stats.activeCases ?? '—', 'purple']].map(([l, v, c]) => (
          <div key={String(l)} className="card p-3 text-center"><p className="text-xs text-slate-400">{l}</p><p className={`text-2xl font-bold text-${c}-600`}>{v}</p></div>
        ))}
      </div>

      <div className="card p-3 mb-4">
        <PatientSearchInput label="Patient" selected={patient} onSelect={setPatient} onClear={() => setPatient(null)} />
      </div>

      {!patient ? (
        <div className="card p-12 text-center text-slate-400"><span className="text-4xl block mb-2">🧠</span>Select a patient above to view assessments, counseling sessions, and care plan</div>
      ) : (
        <div className="flex gap-4">
          <div className="w-80 flex-shrink-0">
            <div className="card overflow-hidden">
              <div className="p-3 bg-slate-50 border-b text-xs font-semibold text-slate-600 uppercase">Psychiatric Assessments</div>
              {assessments.length === 0 ? <div className="p-4 text-center text-slate-400 text-sm">No assessments</div> :
                <div className="divide-y max-h-[65vh] overflow-y-auto">
                  {assessments.map((a: any) => (
                    <div key={a.id} onClick={() => setSelAssessment(a)} className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${selAssessment?.id === a.id ? 'bg-blue-50 border-l-4 border-[#015CA6]' : ''}`}>
                      <p className="text-xs text-slate-400">{fmtDateTime(a.created_at || a.createdAt)}</p>
                      <p className="text-sm font-medium mt-1 line-clamp-2">{a.presenting_complaint || a.presentingComplaint}</p>
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        <Badge label={`SI: ${a.suicide_risk || a.suicideRisk}`} className={`${RISK_CSS[a.suicide_risk || a.suicideRisk] || RISK_CSS.NONE} text-xs`} />
                        <Badge label={`SH: ${a.self_harm_risk || a.selfHarmRisk}`} className={`${RISK_CSS[a.self_harm_risk || a.selfHarmRisk] || RISK_CSS.NONE} text-xs`} />
                      </div>
                    </div>
                  ))}
                </div>}
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto max-h-[70vh]">
            {!selAssessment ? (
              <div className="flex items-center justify-center h-64 text-slate-300">
                <div className="text-center"><p className="text-6xl mb-3">🧠</p><p>Select an assessment to view details, or start a new one</p></div>
              </div>
            ) : (
              <>
                <div className="card p-3 bg-gradient-to-r from-[#02357A] to-indigo-800 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-lg">{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</p>
                      <p className="text-blue-200 text-xs mt-1">{fmtDateTime(selAssessment.created_at || selAssessment.createdAt)}</p>
                    </div>
                    <div className="flex gap-1 items-start">
                      <button onClick={() => { setSessionForm({ ...defaultSessionForm, assessmentId: selAssessment.id }); setShowSession(true); }} className="bg-white/20 hover:bg-white/30 text-xs px-2 py-1 rounded transition-colors">+ Session</button>
                      <FileShaClaimButton
                        patientId={patient.id}
                        patientName={`${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()}
                        patientCrId={patient.sha_beneficiary_cr_id || patient.shaBeneficiaryCrId}
                        encounterId={selAssessment.id}
                        serviceName="Psychiatric assessment"
                        from="mental"
                        className="bg-white/20 hover:bg-white/30 text-xs px-2 py-1 rounded transition-colors"
                      />
                    </div>
                  </div>
                  <p className="text-blue-100 text-sm mt-2 italic">"{selAssessment.presenting_complaint || selAssessment.presentingComplaint}"</p>
                </div>

                <div className="card p-3">
                  <h3 className="font-semibold text-sm mb-2">⚠️ Risk Assessment</h3>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[['Suicide Risk', selAssessment.suicide_risk || selAssessment.suicideRisk], ['Self-Harm Risk', selAssessment.self_harm_risk || selAssessment.selfHarmRisk], ['Harm to Others', selAssessment.harm_to_others_risk || selAssessment.harmToOthersRisk]].map(([l, v]) => (
                      <div key={l} className={`rounded-lg p-2 ${RISK_CSS[v as string] || RISK_CSS.NONE}`}>
                        <p className="text-xs opacity-70">{l}</p>
                        <p className="font-bold">{v}</p>
                      </div>
                    ))}
                  </div>
                  {(selAssessment.risk_notes || selAssessment.riskNotes) && <p className="text-xs text-slate-500 mt-2 bg-slate-50 rounded p-2">{selAssessment.risk_notes || selAssessment.riskNotes}</p>}
                </div>

                <div className="card p-3">
                  <h3 className="font-semibold text-sm mb-2">📋 Diagnosis & Management</h3>
                  {(selAssessment.diagnosis_text || selAssessment.diagnosisText) && <p className="text-sm">{selAssessment.diagnosis_code || selAssessment.diagnosisCode ? `[${selAssessment.diagnosis_code || selAssessment.diagnosisCode}] ` : ''}{selAssessment.diagnosis_text || selAssessment.diagnosisText}</p>}
                  {(selAssessment.management_plan || selAssessment.managementPlan) && <p className="text-xs text-slate-500 mt-1">{selAssessment.management_plan || selAssessment.managementPlan}</p>}
                </div>

                <div className="card p-3">
                  <h3 className="font-semibold text-sm mb-2">💬 Counseling Sessions ({sessions.length})</h3>
                  {sessions.length === 0 ? <p className="text-sm text-slate-400">No sessions recorded</p> :
                    <div className="divide-y">
                      {sessions.map((s: any) => (
                        <div key={s.id} className="py-2 text-sm">
                          <div className="flex justify-between"><span className="font-medium">{s.session_type || s.sessionType}</span><span className="text-xs text-slate-400">{fmtDateTime(s.session_date || s.sessionDate)}</span></div>
                          {s.notes && <p className="text-xs text-slate-500 mt-0.5">{s.notes}</p>}
                          {(s.next_session_date || s.nextSessionDate) && <p className="text-xs text-[#02357A] mt-0.5">📅 Next: {fmtDate(s.next_session_date || s.nextSessionDate)}</p>}
                        </div>
                      ))}
                    </div>}
                </div>

                {plan && (
                  <div className="card p-3">
                    <div className="flex justify-between items-center mb-2"><h3 className="font-semibold text-sm">🎯 Care Plan</h3><button onClick={() => { setPlanForm({ ...defaultPlanForm, ...plan }); setShowPlan(true); }} className="text-xs text-[#02357A] hover:underline">{t('common.edit','Edit')}</button></div>
                    <p className="text-sm">{plan.goals}</p>
                    {plan.interventions && <p className="text-xs text-slate-500 mt-1">{plan.interventions}</p>}
                    {(plan.review_date || plan.reviewDate) && <p className="text-xs text-slate-400 mt-1">Review: {fmtDate(plan.review_date || plan.reviewDate)}</p>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <SlidePanel isOpen={showAssessment} onClose={() => setShowAssessment(false)} title="New Psychiatric Assessment" icon="🧠" size="lg"
        actions={<button onClick={saveAssessment} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Save Assessment'}</button>}>
        <div className="space-y-3">
          {patient && <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-[#02357A]">Patient: <strong>{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</strong></div>}
          <div><label className="label">Presenting Complaint *</label><Textarea rows={2} value={assessmentForm.presentingComplaint} onChange={e => setAssessmentForm({ ...assessmentForm, presentingComplaint: e.target.value })} /></div>
          <div><label className="label">History of Present Illness</label><Textarea rows={2} value={assessmentForm.historyOfPresentIllness} onChange={e => setAssessmentForm({ ...assessmentForm, historyOfPresentIllness: e.target.value })} /></div>
          <div><label className="label">Past Psychiatric History</label><Textarea rows={2} value={assessmentForm.pastPsychiatricHistory} onChange={e => setAssessmentForm({ ...assessmentForm, pastPsychiatricHistory: e.target.value })} /></div>
          <div><label className="label">Substance Use History</label><Input value={assessmentForm.substanceUseHistory} onChange={e => setAssessmentForm({ ...assessmentForm, substanceUseHistory: e.target.value })} /></div>
          <div><label className="label">Family History</label><Input value={assessmentForm.familyHistory} onChange={e => setAssessmentForm({ ...assessmentForm, familyHistory: e.target.value })} /></div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Risk Assessment</p>
            <div className="grid grid-cols-3 gap-2">
              {[['Suicide Risk', 'suicideRisk'], ['Self-Harm Risk', 'selfHarmRisk'], ['Harm to Others', 'harmToOthersRisk']].map(([l, k]) => (
                <div key={k}><label className="label text-xs">{l}</label>
                  <Select value={(assessmentForm as any)[k as string]} onChange={e => setAssessmentForm({ ...assessmentForm, [k as string]: e.target.value })}>
                    {RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
                  </Select>
                </div>
              ))}
            </div>
            <div className="mt-2"><label className="label text-xs">Risk Notes</label><Textarea rows={2} value={assessmentForm.riskNotes} onChange={e => setAssessmentForm({ ...assessmentForm, riskNotes: e.target.value })} /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Diagnosis Code (ICD-11)</label><Input value={assessmentForm.diagnosisCode} onChange={e => setAssessmentForm({ ...assessmentForm, diagnosisCode: e.target.value })} placeholder="e.g. 6A70 Depressive disorder" /></div>
            <div><label className="label">Diagnosis Text</label><Input value={assessmentForm.diagnosisText} onChange={e => setAssessmentForm({ ...assessmentForm, diagnosisText: e.target.value })} /></div>
          </div>
          <div><label className="label">Management Plan</label><Textarea rows={2} value={assessmentForm.managementPlan} onChange={e => setAssessmentForm({ ...assessmentForm, managementPlan: e.target.value })} /></div>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={showSession} onClose={() => setShowSession(false)} title="New Counseling Session" icon="💬"
        actions={<button onClick={saveSession} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Save Session'}</button>}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Session Date/Time</label><Input type="datetime-local" value={sessionForm.sessionDate} onChange={e => setSessionForm({ ...sessionForm, sessionDate: e.target.value })} /></div>
            <div><label className="label">Session Type</label>
              <Select value={sessionForm.sessionType} onChange={e => setSessionForm({ ...sessionForm, sessionType: e.target.value })}>
                {SESSION_TYPES.map(t => <option key={t}>{t}</option>)}
              </Select>
            </div>
          </div>
          <div><label className="label">Duration (minutes)</label><Input type="number" value={sessionForm.durationMinutes} onChange={e => setSessionForm({ ...sessionForm, durationMinutes: e.target.value })} /></div>
          <div><label className="label">{t('common.notes','Notes')}</label><Textarea rows={3} value={sessionForm.notes} onChange={e => setSessionForm({ ...sessionForm, notes: e.target.value })} /></div>
          <div><label className="label">Goals for Next Session</label><Textarea rows={2} value={sessionForm.goalsForNextSession} onChange={e => setSessionForm({ ...sessionForm, goalsForNextSession: e.target.value })} /></div>
          <div><label className="label">Next Session Date</label><Input type="date" value={sessionForm.nextSessionDate} onChange={e => setSessionForm({ ...sessionForm, nextSessionDate: e.target.value })} /></div>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={showPlan} onClose={() => setShowPlan(false)} title="Mental Health Care Plan" icon="🎯"
        actions={<button onClick={savePlan} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Save Care Plan'}</button>}>
        <div className="space-y-3">
          <div><label className="label">Goals</label><Textarea rows={3} value={planForm.goals} onChange={e => setPlanForm({ ...planForm, goals: e.target.value })} /></div>
          <div><label className="label">Interventions</label><Textarea rows={3} value={planForm.interventions} onChange={e => setPlanForm({ ...planForm, interventions: e.target.value })} /></div>
          <div><label className="label">Review Date</label><Input type="date" value={planForm.reviewDate} onChange={e => setPlanForm({ ...planForm, reviewDate: e.target.value })} /></div>
          <div><label className="label">{t('common.status','Status')}</label>
            <Select value={planForm.status} onChange={e => setPlanForm({ ...planForm, status: e.target.value })}>
              {['ACTIVE', 'COMPLETED', 'DISCONTINUED'].map(s => <option key={s}>{s}</option>)}
            </Select>
          </div>
        </div>
      </SlidePanel>
    </Layout>
  );
}
